// Shop between rounds (Design §7): swap components for match points, then "Fertig".
import type { NetClient } from '../pkg/client_wasm.js';
import { CHALLENGE_REWARD } from './challenge.js';
import { el } from './dom.js';
import { sound } from './sound.js';
import { onPadStart } from './input.js';
import { BONUS_NAMES } from './underdog.js';
import { playerMark } from './colors.js';
import { COLOR, LOOK_SLOTS } from './looks.js';

interface Part {
  name: string;
  pro: string;
  con: string;
}

const STANDARD: Part = { name: 'Standard', pro: 'balanced', con: '' };

/** Per slot, components in wire order (`sim::Loadout`). */
export const SLOTS: { title: string; parts: Part[] }[] = [
  {
    title: 'Cannon',
    parts: [
      STANDARD,
      { name: 'Heavy cannon', pro: 'double damage', con: 'long reload, slow bullet' },
      { name: 'Rapid fire', pro: 'burst from a 4-round magazine', con: 'half damage, 0.8 s pause after the burst, more spread, shorter range' },
      { name: 'Precision barrel', pro: 'no spread, fast bullet', con: 'no bounces, reloads a little slower' },
      { name: 'Scatter cannon', pro: '3 bullets per shot', con: 'short range, half damage, more spread' },
      { name: 'Drum', pro: 'up to 6 instead of 4 bullets in play at once, fired one by one', con: 'longer reload' },
      { name: 'Charge cannon', pro: 'holding charges it: up to 2.9 damage (a Standard tank just survives), fast bullet', con: '0.5 damage uncharged, fires only on release' },
      { name: 'Twin', pro: '2 parallel bullets', con: '1.3 s reload after 2 double shots' },
    ],
  },
  {
    title: 'Armor',
    parts: [
      { ...STANDARD, pro: '4 HP' },
      { name: 'Heavy', pro: '5.5 HP', con: 'slow, sluggish turning' },
      { name: 'Light', pro: 'fast', con: '3.5 HP' },
      { name: 'Reactive', pro: 'blocks the first 2 hits each round but for 40 % of the damage', con: 'slower while tiles are still attached' },
      { name: 'Front', pro: 'half damage from the front', con: 'double from behind' },
      { name: 'Ram', pro: '5 HP, ramming does double damage, your own front takes only half when ramming', con: 'shots spread 3× as much, only ¾ damage' },
    ],
  },
  {
    title: 'Drive',
    parts: [
      STANDARD,
      { name: 'Sport', pro: 'high speed', con: 'slides in curves, more spread while driving, turns in place at only ¼ speed' },
      { name: 'Tracks+', pro: 'turns twice as fast in place, 1.5× as fast while moving, full speed in reverse', con: 'slow' },
      { name: 'Hover', pro: 'unaffected by oil and frost on the ground, does not set off mines', con: 'always slides a little, slow to start and slow to stop' },
    ],
  },
  {
    title: 'Ammo',
    parts: [
      { ...STANDARD, pro: '3 bounces' },
      { name: 'Rubber', pro: 'fast, 6 bounces, each +¼ damage and faster, flies longer', con: '¾ damage on a direct hit' },
      { name: 'Explosive', pro: 'area damage, wider than a mine', con: 'no bounces, hits you too' },
      { name: 'Shrapnel', pro: 'bursts into 3 fragments at a wall', con: 'no bounces, fragments hit you too' },
      { name: 'Piercing', pro: 'goes through the first tank', con: '1 bounce' },
      { name: 'Sticky charge', pro: 'sticks to a wall or tank, explodes after 0.7 s, wider on a tank; a tank it destroys blows up too', con: 'no bounces, hits you too' },
      { name: 'Poison', pro: 'half as much again as poison over 2 s, blocks Repair', con: '¾ damage at once' },
      { name: 'Impact', pro: 'a hit knocks the opponent away', con: 'slow bullet' },
      { name: 'Magnet', pro: 'curves slightly toward nearby opponents, more strongly toward heavy/reactive tanks', con: '¾ damage' },
      { name: 'Oil', pro: 'a hit makes the target slide and leave an oil trail; a miss leaves a big oil slick (8 s)', con: '¾ damage, 1 bounce, big slicks catch you too' },
      { name: 'Frost', pro: 'a hit slows the target for 2 s and leaves a frost trail; a miss leaves a big frost patch (8 s) that slows', con: '¾ damage, 1 bounce, big patches catch you too' },
      { name: 'Napalm', pro: 'a hit burns half as much again over 2 s and leaves a fire trail; a miss leaves a big fire (4 s); fire burns 0.1 HP/s, hover tanks too', con: '¾ damage, 1 bounce, big fires catch you too' },
    ],
  },
];

/**
 * The daily challenge (Design §11.1) in words from its wire form: weapon slot and part, target
 * slot and part (wire order); empty when unknown (all 0).
 */
export function challengeText(c: ArrayLike<number>): string {
  const [ws, wp, ts, tp] = Array.from(c);
  const part = (slot: number, k: number) => SLOTS[slot]?.parts[k]?.name;
  const weapon = part(ws, wp);
  const target = part(ts, tp);
  if (!wp || !tp || !weapon || !target) return '';
  const using = ws === 3 ? `${weapon} ammo` : weapon;
  const kind = ts === 2 ? `${target} drive` : `${target} armor`;
  return `Destroy a tank with ${kind} using ${using}`;
}

/** Slots in shop order: cannon, ammo, armor, drive. */
export const SHOWN = [0, 3, 1, 2];


/** Own choice in the open shop, paid only on "Fertig" (dropped when time runs out). */
let choice: { round: number; parts: number[] } | undefined;
/** Round whose "Fertig" was sent by itself (not even one swap affordable), until the server confirms it. */
let autoReady: number | undefined;

/** Fills `panel` with the current shop state; returns the element showing the seconds left. */
/**
 * The shop's “Done” (offline “Go”) button while it is open; Return (Wunsch User 2026-10-21) and a
 * controller's Start press it.
 */
let shopDone: HTMLButtonElement | undefined;
const pressDone = (): boolean => {
  if (!shopDone?.isConnected || shopDone.disabled || !shopDone.offsetParent) return false;
  shopDone.click();
  return true;
};
window.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && !e.repeat && pressDone()) e.preventDefault();
});
onPadStart(pressDone);

export function renderShop(
  panel: HTMLElement,
  net: NetClient,
  send: (bytes: Uint8Array) => void,
  tankName: (i: number) => string,
  tankColor: (i: number) => string,
): HTMLElement {
  const counts = net.component_counts();
  console.assert(
    SLOTS.every((s, i) => s.parts.length === counts[i]),
    'shop texts match the components',
  );
  const you = net.you();
  // Redrawn on every change (a pick, others getting ready): keep where it was scrolled to.
  const restore = panel.hidden ? undefined : keepScroll(panel);
  const timer = el('b', { textContent: String(net.shop_seconds(performance.now())) });
  const head = el('h2', {}, `Shop · Round ${net.round_number() + 1}/${net.round_count()} starts in `, timer, ' s');
  const ready = net.ready_flags();
  const looks = net.looks();
  const readyRow = el(
    'div',
    { className: 'ready' },
    ...[...ready].map((r, i) => el('span', { className: i === you ? 'you' : '' }, playerMark(tankColor(i), looks[i * LOOK_SLOTS + COLOR] ?? 0), tankName(i), r ? ' ✓' : ' …')),
  );
  if (you < 0) {
    panel.replaceChildren(head, el('p', { className: 'hint', textContent: 'The players are swapping parts.' }), readyRow);
    return timer;
  }

  const totals = [...net.totals()];
  const points = totals[you] ?? 0;
  /** Own place in the match ranking with `own` points (ties share a place, like the board). */
  const rank = (own: number) => 1 + totals.filter((t, i) => i !== you && t > own).length;
  const next = net.swap_cost(1);
  const done = ready[you] === 1;
  const installed = [...net.loadout(you)];
  if (done || choice?.round !== net.round_number()) choice = { round: net.round_number(), parts: [...installed] };
  const parts = choice.parts;
  // Underdog bonus for the next round: only its name, on a brass plaque (Design §6.5).
  const bonus = BONUS_NAMES[net.bonus()];
  if (done) autoReady = undefined;
  // Not even one swap affordable: done by itself — but not over a plaque, it would vanish unseen.
  else if (points < next && !bonus && autoReady !== net.round_number()) {
    autoReady = net.round_number();
    send(net.ready_with(Uint8Array.from(installed)));
  }
  const price = (chosen: number[]) => net.swap_cost(chosen.filter((c, slot) => c !== installed[slot]).length);

  const finish = el('button', {
    className: 'primary big',
    onclick: () => {
      sound(price(parts) > 0 ? 'buy' : 'done');
      send(net.ready_with(Uint8Array.from(parts)));
    },
  });
  shopDone = finish;
  const info = el('p');
  const slotsBox = slotGrid();
  const update = () => {
    const total = price(parts);
    finish.textContent = done ? 'Done ✓' : total ? `Done (−${total} points)` : 'Done';
    finish.disabled = done;
    const after = points - total;
    info.replaceChildren(
      'Your points: ',
      el('b', { textContent: String(points) }),
      ` · Place ${rank(points)}`,
      total
        ? el(
            'span',
            { className: rank(after) > rank(points) ? 'worse' : '' },
            ` → after buying ${after} ${after === 1 ? 'point' : 'points'} · Place ${rank(after)}`,
          )
        : '',
    );
    fillSlots(slotsBox, (slot, c) => {
      const chosen = parts[slot] === c;
      const need = price(parts.map((old, k) => (k === slot ? c : old)));
      return partCard(
        'button',
        slot,
        c,
        {
          className: chosen ? 'part current' : 'part',
          disabled: chosen || done || need > points,
          title: chosen ? 'selected' : need > points ? `Not enough points (${need} needed)` : `This choice then costs ${need} points`,
          onclick: () => {
            parts[slot] = c;
            sound('click');
            update();
          },
        },
        installed[slot] === c
          ? el('span', { className: 'tag', textContent: ' · installed' })
          : need > price(parts) && el('span', { className: 'cost', textContent: ` · −${need - price(parts)}` }),
      );
    });
  };
  update();
  // One sentence per line, the price first and stronger.
  const warn = el(
    'p',
    { className: 'warn' },
    el('b', {
      textContent: `Your next swap costs ${next} points, each one after that one more – the same points the match is scored by.`,
    }),
    el('span', { textContent: 'Buying can cost you places in the ranking. You pay on “Done”, removed parts are gone; without “Done” the choice is lost.' }),
  );
  const plaque = bonus ? el('div', { className: 'plaque' }, el('span', { textContent: bonus })) : '';
  // The daily challenge, to buy for it (Design §11.1).
  const task = challengeText(net.shop_challenge());
  const challenge = task ? el('p', { className: 'challenge' }, el('b', { textContent: 'Daily challenge: ' }), `${task} (+${CHALLENGE_REWARD} reward points)`) : '';
  panel.replaceChildren(el('div', { className: 'head' }, head, finish), el('div', { className: 'status' }, info, readyRow), challenge, slotsBox, warn, plaque);
  restore?.();
  return timer;
}

/** Shop for the local game: every part free, the choice counts for both players; `start` gets it on "Los". */
export function renderFreeShop(panel: HTMLElement, start: (parts: number[]) => void): void {
  const parts = SLOTS.map(() => 0);
  const slotsBox = slotGrid();
  const update = () =>
    fillSlots(slotsBox, (slot, c) =>
      partCard('button', slot, c, {
        className: parts[slot] === c ? 'part current' : 'part',
        disabled: parts[slot] === c,
        onclick: () => {
          parts[slot] = c;
          sound('click');
          update();
        },
      }),
    );
  update();
  const go = el('button', { className: 'primary big', textContent: 'Go', onclick: () => start([...parts]) });
  shopDone = go;
  panel.replaceChildren(
    el('div', { className: 'head' }, el('h2', { textContent: 'Shop · all parts free' }), go),
    el('p', { className: 'hint', textContent: 'The choice applies to both players.' }),
    slotsBox,
  );
}

/** Empty grid for the slot columns in shop order. */
function slotGrid(): HTMLElement {
  return el('div', { className: 'slots' });
}

/** Fills `box` with one column per slot, each scrolling on its own when long (Wunsch User
 * 2026-10-26); `card` makes the button for component `c` of `slot`. */
function fillSlots(box: HTMLElement, card: (slot: number, c: number) => HTMLElement): void {
  const restore = keepScroll(box);
  box.replaceChildren(
    ...SHOWN.map((slot) => {
      const s = SLOTS[slot];
      const list = el('div', { className: 'parts' }, ...s.parts.map((_, c) => card(slot, c)));
      return el('div', { className: 'slot' }, el('h3', { textContent: s.title }), list);
    }),
  );
  restore();
}

/** Scroll positions of `root` and its slot columns, put back by the returned function after
 * they were drawn anew (Bug User 2026-10-26: a pick scrolled the column back to the top). */
function keepScroll(root: HTMLElement): () => void {
  const scrolled = () => [root, ...root.querySelectorAll<HTMLElement>('.parts')];
  const tops = scrolled().map((e) => e.scrollTop);
  return () => scrolled().forEach((e, k) => (e.scrollTop = tops[k] ?? 0));
}

/** Card for component `c` of `slot` (shop button, guide entry): name, `tag` behind it, pros and cons. */
export function partCard<K extends 'button' | 'div'>(
  element: K,
  slot: number,
  c: number,
  props: Partial<HTMLElementTagNameMap[K]>,
  tag?: Node | string | false,
): HTMLElement {
  const p = SLOTS[slot].parts[c];
  return el(
    element,
    props,
    el('b', { textContent: p.name }),
    tag,
    // Sign and text side by side, so wrapped lines stay under the text.
    el('div', { className: 'pro' }, el('span', { textContent: '+' }), el('span', { textContent: p.pro })),
    p.con && el('div', { className: 'con' }, el('span', { textContent: '−' }), el('span', { textContent: p.con })),
  );
}
